import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import styled from "styled-components";
import * as yup from "yup";

// Utils
import {COMPONENT, deepCopyBuilder, GROUP} from "../../utils/builder";
import {
  collectTransferDependencyClosure,
  getAncestryName,
  mergeTransferDependenciesIntoBuilder
} from "./helpers";

// Components
import Modal from "../../components/Modal";
import Help from "../../components/Help";
import InputSelect from "../../components/form/InputSelect";

// Style
import {
  Button,
  Form,
  FormField,
  HeadingCenter,
  Inline,
  Label,
  List,
  ListItem,
  Pill,
  Warning
} from "../../style/components/general";
import {flex} from "../../style/components/mixins";
import {pad} from "../../style/components/variables";
import voice from "../../style/components/typography";

const ModalTransfer = ({
  visible = false,
  setVisible,
  save,
  sourceBuilder,
  destBuilder,
  targetElement,
  state,
  isState
}) => {
  const schema = yup.object().shape({
    linkedGroup: yup.string().nullable(),
    linkedComponent: yup.string().nullable()
  });

  const form = useForm({resolver: yupResolver(schema)});

  const {watch, handleSubmit} = form;

  const watchLinkedGroup = watch("linkedGroup");

  const {missingDependencyNames, sourceBuilderForLabels} = useMemo(() => {
    const srcById = sourceBuilder?.byId || {};
    const dById = destBuilder?.byId || {};
    const closure = collectTransferDependencyClosure(targetElement.name, srcById, dById);
    const missing = [...closure].filter(n => !(n in dById)).sort();

    return {
      missingDependencyNames: missing,
      sourceBuilderForLabels: {byId: srcById, allIds: sourceBuilder?.allIds || []}
    };
  }, [destBuilder, sourceBuilder, targetElement]);

  const handleMoveFields = ({linkedGroup, linkedComponent}) => {
    const srcById = sourceBuilder?.byId || {};
    const dById = destBuilder?.byId || {};
    const closure = collectTransferDependencyClosure(targetElement.name, srcById, dById);
    const namesToMerge = [...closure].filter(n => !(n in (destBuilder.byId || {})));

    const updated = deepCopyBuilder(destBuilder);
    const newParentName = linkedComponent || linkedGroup || "";

    mergeTransferDependenciesIntoBuilder(updated, srcById, namesToMerge, newParentName);

    updated.byId[targetElement.name] = {
      ...targetElement,
      parentName: newParentName in updated.byId ? newParentName : ""
    };

    if (newParentName && updated.byId[newParentName]) {
      const parent = updated.byId[newParentName];
      const ch = [...(parent.children || [])];
      if (!ch.includes(targetElement.name))
        updated.byId[newParentName] = {...parent, children: [...ch, targetElement.name]};
    } else if (!updated.allIds.includes(targetElement.name)) {
      updated.allIds.push(targetElement.name);
    }

    save(updated);
    setVisible(false);
  };

  const renderFieldOptions = () => {
    const linkedGroups = destBuilder.allIds
      .map(id => destBuilder.byId[id])
      .filter(el => el?.element === GROUP && el?.toggle);
    const linkedComponents =
      watchLinkedGroup && destBuilder.byId[watchLinkedGroup]?.children
        ? destBuilder.byId[watchLinkedGroup].children
            .map(id => destBuilder.byId[id])
            .filter(el => el?.element === COMPONENT && el?.toggle)
        : [];

    return (
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleMoveFields)} noValidate>
          {linkedGroups.length > 0 && (
            <FormField>
              <InputSelect
                name="linkedGroup"
                options={linkedGroups}
                placeholder="Link to Group..."
                label="Linked Group"
              />
            </FormField>
          )}
          {watchLinkedGroup && linkedComponents.length > 0 && (
            <FormField>
              <InputSelect
                name="linkedComponent"
                options={linkedComponents}
                placeholder="Link to Component..."
                label="Linked Component"
              />
            </FormField>
          )}
          <Button type="submit">Save</Button>
        </Form>
      </FormProvider>
    );
  };

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <TitleRow>
        <HeadingCenter>Copy Field</HeadingCenter>
        <Help title="Copy field">
          Copies this field into the other state&apos;s builder for this checksheet. The field
          becomes independent in each state &ndash; editing or removing it in one does not affect
          the other.
        </Help>
      </TitleRow>
      <Item>
        <Label bold>Field: </Label>
        {targetElement.label}
      </Item>
      <Item>
        <Label bold>From: </Label>
        <Pill color={isState ? `#${state.color}` : undefined} basic>
          {isState ? state.name : "DEFAULT"} State
        </Pill>
      </Item>
      <Item>
        <Label bold>To: </Label>
        <Pill color={isState ? undefined : `#${state.color}`} basic>
          {isState ? "DEFAULT" : state.name} State
        </Pill>
      </Item>
      {missingDependencyNames.length > 0 && (
        <WarningWrapper>
          <br />
          <WarningHeader>Warning!</WarningHeader>&nbsp;The following elements are required by this
          field but are not in the destination state yet. They will be copied from the source state
          as part of this transfer:
          <br />
          <DependencyList>
            {missingDependencyNames.map(name => {
              const element = sourceBuilder.byId[name];
              return (
                <ListItem key={name}>
                  {element
                    ? getAncestryName(element, sourceBuilderForLabels)
                    : name.replace(/-/g, " ")}
                </ListItem>
              );
            })}
          </DependencyList>
          <br />
          <br />
        </WarningWrapper>
      )}
      {renderFieldOptions()}
    </Modal>
  );
};

ModalTransfer.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  sourceBuilder: PropTypes.objectOf(PropTypes.any).isRequired,
  destBuilder: PropTypes.objectOf(PropTypes.any).isRequired,
  targetElement: PropTypes.objectOf(PropTypes.any).isRequired,
  state: PropTypes.objectOf(PropTypes.any).isRequired,
  isState: PropTypes.bool.isRequired
};

// Style Overrides
const TitleRow = styled.div`
  ${flex("row", "nowrap", "center", "center")};
  gap: ${pad / 2}px;

  ${HeadingCenter} {
    margin: 0 0 ${pad * 2}px;
  }
`;

const Item = styled(Inline)`
  margin-bottom: ${pad / 2}px;
`;

const DependencyList = styled(List)`
  margin: ${pad / 2}px 0 0 ${pad}px;
  padding: 0 0 0 ${pad}px;
`;

const WarningWrapper = styled(Warning)`
  margin-bottom: ${pad / 2}px;
  color: ${({theme}) => theme.secondary};

  ${voice.normal}
`;

const WarningHeader = styled.strong`
  color: ${({theme}) => theme.warning};
`;

export default ModalTransfer;
